/**
 * Notification sound effects (iter137.1).
 *
 * Plays HTMLAudioElement-backed clips loaded from /sounds/ so the
 * user can swap the audio out manually:
 *
 *   • /app/frontend/public/sounds/conv-tick.wav      — soft tick for any new message
 *   • /app/frontend/public/sounds/conv-mention.wav   — chime for @mentions
 *
 * To replace either sound: drop a same-named file (.wav / .mp3 / .ogg
 * — any browser-decodable format) into /app/frontend/public/sounds/
 * and refresh. Optionally rename and update the constants below.
 *
 * If a clip fails to load (e.g. file deleted), the helper falls back
 * to a tiny WebAudio-synth beep so the audible cue is never silent.
 *
 * Master toggle persists in localStorage under ``conv:sound_enabled``
 * (default: true). 1.5s throttle prevents burst-spam.
 */
const STORAGE_KEY = "conv:sound_enabled";
const TICK_URL = "/sounds/conv-tick.wav";
const MENTION_URL = "/sounds/conv-mention.wav";

let _tickEl = null;
let _mentionEl = null;
let _ctx = null;
let _lastPlayedAt = 0;

function _audio(url) {
  // Browsers pre-buffer the entire clip on first construction so the
  // first play() doesn't wait on the network. Reusing the same
  // element across plays is required to allow rapid-fire playback
  // (cloning each time would leak DOM nodes).
  try {
    const a = new Audio(url);
    a.preload = "auto";
    a.crossOrigin = "anonymous";
    return a;
  } catch {
    return null;
  }
}

function _ensureElements() {
  if (!_tickEl) _tickEl = _audio(TICK_URL);
  if (!_mentionEl) _mentionEl = _audio(MENTION_URL);
}

function _audioCtx() {
  if (_ctx) return _ctx;
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    _ctx = new Ctor();
    return _ctx;
  } catch {
    return null;
  }
}

function _synthBeep({ freq, duration, gain = 0.08, delay = 0 } = {}) {
  const ctx = _audioCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume?.();
  const start = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(freq, start);
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(gain, start + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  osc.connect(g);
  g.connect(ctx.destination);
  osc.start(start);
  osc.stop(start + duration + 0.02);
}

export function isSoundEnabled() {
  try {
    const v = window.localStorage?.getItem(STORAGE_KEY);
    return v === null ? true : v === "1" || v === "true";
  } catch {
    return true;
  }
}

export function setSoundEnabled(enabled) {
  try {
    window.localStorage?.setItem(STORAGE_KEY, enabled ? "1" : "0");
    if (enabled) _audioCtx()?.resume?.();
  } catch { /* noop */ }
  try {
    window.dispatchEvent(new CustomEvent("conv:sound_toggled", { detail: { enabled } }));
  } catch { /* noop */ }
}

function _throttle() {
  const now = Date.now();
  if (now - _lastPlayedAt < 1500) return false;
  _lastPlayedAt = now;
  return true;
}

function _play(audioEl, fallback) {
  if (!audioEl) { fallback(); return; }
  try {
    audioEl.currentTime = 0;
    const p = audioEl.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => fallback());
    }
  } catch {
    fallback();
  }
}

export function playUnreadTick() {
  if (!isSoundEnabled()) return;
  if (!_throttle()) return;
  _ensureElements();
  _play(_tickEl, () => _synthBeep({ freq: 660, duration: 0.12, gain: 0.06 }));
}

export function playMentionChime() {
  if (!isSoundEnabled()) return;
  if (!_throttle()) return;
  _ensureElements();
  _play(_mentionEl, () => {
    _synthBeep({ freq: 880, duration: 0.16, gain: 0.10 });
    _synthBeep({ freq: 1320, duration: 0.20, gain: 0.10, delay: 0.11 });
  });
}

/** Manually preview a sound — used by the AccountMenu sound toggle so
 *  the user can hear what they just enabled. */
export function previewSound(kind = "tick") {
  // Bypass throttle for explicit previews.
  _lastPlayedAt = 0;
  if (kind === "mention") playMentionChime();
  else playUnreadTick();
}
